import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { NumberInput } from '@/components/ui/number-input';
import {
  getResults,
  getIndividualPicks,
  setMyIndividualPick,
  removeMyIndividualPick,
  updateSelection,
  type CandidateHappening,
} from '@/lib/selections-api';
import { updateHappening } from '@littlebooker/dal';
import type { Selection, SelectionRound, SelectionVoter } from '@littlebooker/dal';

interface EditorialItem {
  happening_id: string;
  reason?: string;
}
interface PublicationSettings {
  consensus_n?: number;
  pinned?: EditorialItem[];
  excluded?: EditorialItem[];
}

interface PublicationTabProps {
  selectionId: string;
  selection: Selection;
  rounds: SelectionRound[];
  candidates: CandidateHappening[];
  voters: SelectionVoter[];
  isOwner: boolean;
  isCurrentUserVoter: boolean;
  currentUserId: string | undefined;
  userName: (uid: string) => string;
  targetFinal: number;
  onChanged: () => void;
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-3 py-2';

export function PublicationTab(props: PublicationTabProps) {
  const { rounds } = props;
  const revealedAllocate = [...rounds]
    .sort((a, b) => a.ordinal - b.ordinal)
    .filter((r) => r.kind === 'allocate' && r.state === 'revealed')
    .pop();

  return (
    <div className="space-y-6 pt-4">
      {revealedAllocate ? (
        <ConsensusSection {...props} allocateRoundId={revealedAllocate.id} />
      ) : (
        <p className="text-sm text-muted-foreground">
          Consensus picks are available once round 2 (allocate) is revealed.
        </p>
      )}
      <IndividualPicksSection {...props} />
    </div>
  );
}

function ConsensusSection({
  selectionId,
  selection,
  candidates,
  isOwner,
  targetFinal,
  allocateRoundId,
  onChanged,
}: PublicationTabProps & { allocateRoundId: string }) {
  const { data: results } = useQuery({
    queryKey: ['selection-results', allocateRoundId],
    queryFn: () => getResults(selectionId, allocateRoundId),
  });

  const pub = (selection.settings as { publication?: PublicationSettings })?.publication ?? {};
  const [n, setN] = useState(pub.consensus_n ?? targetFinal);
  const [excluded, setExcluded] = useState<Record<string, string>>(
    Object.fromEntries((pub.excluded ?? []).map((e) => [e.happening_id, e.reason ?? ''])),
  );
  const [pinned, setPinned] = useState<Record<string, string>>(
    Object.fromEntries((pub.pinned ?? []).map((p) => [p.happening_id, p.reason ?? ''])),
  );

  const title = (hid: string) => candidates.find((c) => c.id === hid)?.title ?? hid;
  const ranked = results?.rows ?? [];
  const totalScore = results?.ballotCount || 1;

  // Published consensus = ranked minus excluded, top N, plus pinned.
  const published = useMemo(() => {
    const base = ranked.filter((r) => !(r.happening_id in excluded)).slice(0, n);
    const baseIds = new Set(base.map((r) => r.happening_id));
    const pinnedRows = Object.keys(pinned)
      .filter((id) => !baseIds.has(id))
      .map((id) => ranked.find((r) => r.happening_id === id))
      .filter((r): r is NonNullable<typeof r> => !!r);
    return [...base, ...pinnedRows];
  }, [ranked, excluded, pinned, n]);

  const save = useMutation({
    mutationFn: () =>
      updateSelection(selectionId, {
        settings: {
          ...selection.settings,
          publication: {
            consensus_n: n,
            excluded: Object.entries(excluded).map(([happening_id, reason]) => ({ happening_id, reason })),
            pinned: Object.entries(pinned).map(([happening_id, reason]) => ({ happening_id, reason })),
          },
        },
      }),
    onSuccess: onChanged,
  });

  // Staff picks live on each happening (metadata.is_staff_pick) — the public site reads
  // them there, independently of this selection. Applying is a one-way push: it writes the
  // current published list onto those happenings, it does not keep them in sync afterwards.
  const publishedIds = published.map((r) => r.happening_id);
  const staffPickState = (hid: string) => candidates.find((c) => c.id === hid)?.is_staff_pick === true;
  const alreadyMarked = publishedIds.filter(staffPickState).length;

  const applyStaffPicks = useMutation({
    mutationFn: async (value: boolean) => {
      const targets = publishedIds.filter((hid) => staffPickState(hid) !== value);
      for (const hid of targets) {
        await updateHappening(hid, { metadata: { is_staff_pick: value } });
      }
      return targets.length;
    },
    onSuccess: onChanged,
  });

  const toggleExclude = (id: string) =>
    setExcluded((prev) => {
      const next = { ...prev };
      if (id in next) delete next[id];
      else next[id] = '';
      return next;
    });
  const togglePin = (id: string) =>
    setPinned((prev) => {
      const next = { ...prev };
      if (id in next) delete next[id];
      else next[id] = '';
      return next;
    });

  // Editorial controls cover the top N plus a buffer below the line.
  const editableRows = ranked.slice(0, n + 8);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Consensus picks</CardDescription>
          <CardTitle className="text-lg">{published.length} published</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <ol className="list-inside list-decimal text-sm">
            {published.map((r) => (
              <li key={r.happening_id}>
                {title(r.happening_id)}
                {r.happening_id in pinned && (
                  <span className="ml-2 rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">
                    pinned
                  </span>
                )}{' '}
                <span className="text-muted-foreground">
                  ({((r.score / totalScore) * 100).toFixed(1)}%)
                </span>
              </li>
            ))}
          </ol>
          {published.length === 0 && (
            <p className="text-sm text-muted-foreground">No consensus picks yet.</p>
          )}

          {isOwner && published.length > 0 && (
            <div className="space-y-2 border-t pt-3">
              <p className="text-xs text-muted-foreground">
                {alreadyMarked === published.length
                  ? 'All of these are marked as staff picks on the public site.'
                  : `${alreadyMarked} of ${published.length} are marked as staff picks on the public site.`}{' '}
                Applying writes the flag onto each show now — later edits to this list won't
                follow.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => applyStaffPicks.mutate(true)}
                  disabled={applyStaffPicks.isPending || alreadyMarked === published.length}
                >
                  {applyStaffPicks.isPending ? 'Applying…' : '✨ Mark these as staff picks'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => applyStaffPicks.mutate(false)}
                  disabled={applyStaffPicks.isPending || alreadyMarked === 0}
                >
                  Clear staff picks
                </Button>
              </div>
              {applyStaffPicks.isError && (
                <p className="text-xs text-destructive">
                  Failed to update staff picks. Some shows may have been updated — retry to finish.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {isOwner && (
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Editorial pass (owner)</CardDescription>
            <CardTitle className="text-base">Adjust separately from the scores</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <label className="text-sm">Consensus N</label>
              <NumberInput
                min={1}
                allowEmpty={false}
                className="w-20 rounded-md border border-input bg-background px-2 py-1"
                value={n}
                onChange={(v) => setN(v ?? 1)}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Scores are fixed. Exclude a show from the published list, or pin one in from below the
              line — each with a reason. These are recorded as editorial adjustments, kept distinct
              from the vote.
            </p>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">#</TableHead>
                    <TableHead>Show</TableHead>
                    <TableHead className="w-20">Share</TableHead>
                    <TableHead className="w-56">Editorial</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editableRows.map((r, i) => {
                    const inTopN = i < n;
                    const isExcluded = r.happening_id in excluded;
                    const isPinned = r.happening_id in pinned;
                    return (
                      <TableRow key={r.happening_id}>
                        <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                        <TableCell className="font-medium">{title(r.happening_id)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {((r.score / totalScore) * 100).toFixed(1)}%
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {inTopN ? (
                              <Button
                                size="sm"
                                variant={isExcluded ? 'default' : 'outline'}
                                onClick={() => toggleExclude(r.happening_id)}
                              >
                                {isExcluded ? 'Excluded' : 'Exclude'}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant={isPinned ? 'default' : 'outline'}
                                onClick={() => togglePin(r.happening_id)}
                              >
                                {isPinned ? 'Pinned' : 'Pin'}
                              </Button>
                            )}
                            {isExcluded && (
                              <input
                                className={inputClass}
                                placeholder="Reason"
                                value={excluded[r.happening_id]}
                                onChange={(e) =>
                                  setExcluded((p) => ({ ...p, [r.happening_id]: e.target.value }))
                                }
                              />
                            )}
                            {isPinned && (
                              <input
                                className={inputClass}
                                placeholder="Reason"
                                value={pinned[r.happening_id]}
                                onChange={(e) =>
                                  setPinned((p) => ({ ...p, [r.happening_id]: e.target.value }))
                                }
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save editorial'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function IndividualPicksSection({
  selectionId,
  candidates,
  voters,
  isCurrentUserVoter,
  currentUserId,
  userName,
  onChanged,
}: PublicationTabProps) {
  const queryClient = useQueryClient();
  const { data: picks = [] } = useQuery({
    queryKey: ['selection-individual-picks', selectionId],
    queryFn: () => getIndividualPicks(selectionId),
  });

  const byVoter = new Map(picks.map((p) => [p.voter_id, p]));
  const mine = currentUserId ? byVoter.get(currentUserId) : undefined;

  const [happeningId, setHappeningId] = useState('');
  const [justification, setJustification] = useState('');
  // Seed the editor from the saved pick when it loads/changes.
  useEffect(() => {
    if (mine) {
      setHappeningId(mine.happening_id);
      setJustification(mine.justification ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mine?.id]);

  const title = (hid: string) => candidates.find((c) => c.id === hid)?.title ?? hid;

  const savePick = useMutation({
    mutationFn: () => setMyIndividualPick(selectionId, { happening_id: happeningId, justification }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['selection-individual-picks', selectionId] });
      onChanged();
    },
  });
  const clearPick = useMutation({
    mutationFn: () => removeMyIndividualPick(selectionId),
    onSuccess: () => {
      setHappeningId('');
      setJustification('');
      queryClient.invalidateQueries({ queryKey: ['selection-individual-picks', selectionId] });
      onChanged();
    },
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <CardDescription>Individual picks</CardDescription>
          <CardTitle className="text-base">One per voter, published under their name</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {voters.map((v) => {
            const pick = byVoter.get(v.user_id);
            return (
              <div key={v.user_id} className="border-b pb-2 last:border-0">
                <p className="text-sm font-medium">{userName(v.user_id)}</p>
                {pick ? (
                  <p className="text-sm text-muted-foreground">
                    {title(pick.happening_id)}
                    {pick.justification ? ` — ${pick.justification}` : ''}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">No pick yet.</p>
                )}
              </div>
            );
          })}
          {voters.length === 0 && <p className="text-sm text-muted-foreground">No voters.</p>}
        </CardContent>
      </Card>

      {isCurrentUserVoter && (
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Your individual pick</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="text-sm font-medium">Show (from the full scope)</label>
              <select
                className={inputClass}
                value={happeningId}
                onChange={(e) => setHappeningId(e.target.value)}
              >
                <option value="">Select…</option>
                {candidates.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Why (published)</label>
              <textarea
                className={inputClass}
                rows={2}
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => savePick.mutate()}
                disabled={!happeningId || savePick.isPending}
              >
                {savePick.isPending ? 'Saving…' : 'Save my pick'}
              </Button>
              {mine && (
                <Button size="sm" variant="outline" onClick={() => clearPick.mutate()} disabled={clearPick.isPending}>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
